import { useState, type ChangeEvent } from "react";
import { ShieldCheck } from "lucide-react";
import type { ConfirmationState } from "../../types/payrollAdmin.js";
import { Button, Input, Modal } from "../ui/index.js";

export const ConfirmDialog = ({
  confirmation,
  onClose,
}: {
  confirmation: ConfirmationState;
  onClose: () => void;
}) => {
  const [reason,setReason] = useState("");
  const handleConfirm = () => {
    if (confirmation.requiresReason && reason.trim().length < 8) return;
    confirmation.onConfirm(reason.trim());
    onClose();
  };

  return (
    <Modal
      open
      onOpenChange={(open: boolean) => {
        if (!open) onClose();
      }}
      title={confirmation.title}
      description={
        confirmation.tone === "danger"
          ? "Review this sensitive payroll action before continuing."
          : "Review this payroll action before continuing."
      }
      size="md"
      closeLabel="Close confirmation"
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex min-h-11 cursor-pointer items-center justify-center rounded-lg border border-border px-4 text-sm font-medium text-foreground-muted transition-colors hover:bg-elevated hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            Cancel
          </button>
          <Button
            type="button"
            size="lg"
            variant={confirmation.tone === "danger" ? "danger" : "primary"}
            onClick={handleConfirm}
            disabled={confirmation.requiresReason && reason.trim().length < 8}
          >
            {confirmation.confirmLabel}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 pt-5">
        <p className="text-sm leading-6 text-foreground-muted">
          {confirmation.description}
        </p>
        {confirmation.requiresReason && <Input id="bulk-unlock-reason" label="Reason for unlocking" value={reason} onChange={(event: ChangeEvent<HTMLInputElement>)=>setReason(event.target.value)} minLength={8} maxLength={500} required hint="At least 8 characters. This reason is recorded for every employee." />}
        <div
          className={`flex items-start gap-3 rounded-xl border p-3 ${confirmation.tone === "danger" ? "border-danger/30 bg-danger-subtle" : "border-accent/25 bg-accent-subtle"}`}
        >
          <span
            className={`flex size-8 shrink-0 items-center justify-center rounded-lg ${confirmation.tone === "danger" ? "bg-danger/15 text-danger" : "bg-accent/15 text-accent"}`}
          >
            <ShieldCheck size={16} aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-foreground">
              {confirmation.checkpointTitle ?? "Payroll checkpoint"}
            </p>
            <p className="mt-0.5 text-xs leading-5 text-foreground-muted">
              {confirmation.checkpointDescription ??
                "Confirm only after reviewing the employee and payroll month shown above."}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
